import apiClient from './apiClient';
import { AuthResponse, VerifyTokenResponse } from '../types/authType';
import { UserType } from '../types/userType';
import { AxiosError } from 'axios';

interface EmailSignupParams {
  email: string;
  password: string;
  username: string;
  bio: string;
}

interface CheckEmailDuplicateResponse {
  data: {
    is_duplicate: boolean;
    email: string;
  };
}

export const checkEmailDuplicate = async (email: string): Promise<boolean> => {
  try {
    const response = await apiClient.post<CheckEmailDuplicateResponse>(
      '/pxauth/duplicate_email/',
      { email },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.data.is_duplicate;
  } catch (error) {
    throw error;
  }
};

export const emailSignup = async (formData: FormData): Promise<UserType> => {
  try {
    const response = await apiClient.post<AuthResponse>(
      '/pxauth/email_signup/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    const { refresh_token, access_token } = response.data.data;
    
    // Store tokens in localStorage
    localStorage.setItem('refreshToken', refresh_token);
    localStorage.setItem('accessToken', access_token);

    // Verify the access token to get user data
    const userData = await verifyAccessToken(access_token);
    
    // Store user data in localStorage and set global user
    setUserData(userData);

    return userData;
  } catch (error) {
    throw error;
  }
};

export const verifyAccessToken = async (accessToken: string): Promise<UserType> => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    const response = await apiClient.post<VerifyTokenResponse>(
      '/pxauth/verify_access_token/', 
      { 
        access_token: accessToken,
        refresh_token: refreshToken
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    // If the response includes new tokens, update them
    if (response.data.data.access_token) {
      localStorage.setItem('accessToken', response.data.data.access_token);
    }
    if (response.data.data.refresh_token) {
      localStorage.setItem('refreshToken', response.data.data.refresh_token);
    }

    // Map the response to match UserType
    return {
      ...response.data.data.user,
      id: String(response.data.data.user.id),
      avatar: response.data.data.user.profile_picture,
      username: response.data.data.user.vivid_username
    };
  } catch (error) {
    // If verification fails completely, clear tokens and throw error
    if (error instanceof AxiosError && error.response?.status === 401) {
      signout();
    }
    throw error;
  }
};

export const emailSignin = async (email: string, password: string): Promise<UserType> => {
  try {
    const response = await apiClient.post<AuthResponse>(
      '/pxauth/email_signin/', 
      { email, password },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    const { refresh_token, access_token } = response.data.data;
    
    // Store tokens in localStorage
    localStorage.setItem('refreshToken', refresh_token);
    localStorage.setItem('accessToken', access_token);

    // Verify the access token to get user data
    const userData = await verifyAccessToken(access_token);
    
    // Store user data in localStorage and set global user
    setUserData(userData);

    return userData;
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  try {
    const response = await apiClient.post(
      '/pxauth/reset_password/', 
      { token, new_password: newPassword },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendResetPasswordEmail = async (email: string): Promise<void> => {
  try {
    const response = await apiClient.post(
      '/pxauth/send_reset_password_email/', 
      { email },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUserDataFromLocalStorage = (): UserType | null => {
  const userDataString = localStorage.getItem('userData');
  return userDataString ? JSON.parse(userDataString) : null;
};

export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('accessToken');
};

export const signout = () => {
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('userData');
  window.user = null;
};

const setUserData = (userData: UserType) => {
  localStorage.setItem('userData', JSON.stringify(userData));
  window.user = userData;
};

export const fetchUserData = async (): Promise<UserType> => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const response = await apiClient.get<{ data: UserType }>('/core/user/');
    
    const userData = response.data.data;
    // Store user data in localStorage
    localStorage.setItem('viUser', JSON.stringify(userData));
    setUserData(userData);
    return userData;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};
